import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { benchmarkApi } from '../../services/api';
import { PageHeader, Spinner, EmptyState } from '../../components/shared/index';
import {
  ArrowPathIcon, CheckCircleIcon, InformationCircleIcon,
  ChartPieIcon, GlobeAltIcon, BuildingOffice2Icon, UsersIcon,
} from '@heroicons/react/24/outline';

const SIZE_BANDS = [
  { value: '1-50',      label: '1–50 employees' },
  { value: '51-300',    label: '51–300 employees' },
  { value: '301-2000',  label: '301–2,000 employees' },
  { value: '2001-5000', label: '2,001–5,000 employees' },
  { value: '5000+',     label: '5,000+ employees' },
];

const SEGMENTS = [
  { key: 'peer',     label: 'Peer',     icon: UsersIcon,            desc: 'Same industry and size' },
  { key: 'industry', label: 'Industry', icon: BuildingOffice2Icon,  desc: 'All sizes in your industry' },
  { key: 'size',     label: 'Size',     icon: ChartPieIcon,         desc: 'All industries at your size' },
  { key: 'global',   label: 'Global',   icon: GlobeAltIcon,         desc: 'Everyone opted-in globally' },
];

const NEGATIVE_METRICS = new Set(['attrition_rate', 'pip_rate', 'em_pct']);

function humaniseMetric(key) {
  const map = {
    x_pct:           '% rated X',
    eplus_pct:       '% rated E+',
    e_pct:           '% rated E',
    em_pct:          '% rated E-',
    attrition_rate:  'Attrition rate',
    pip_rate:        'PIP rate',
    avg_tenure:      'Avg. tenure (yrs)',
    avg_score:       'Average score',
    promotion_rate:  'Promotion rate',
    engagement:      'Engagement score',
    manager_span:    'Manager span of control',
  };
  if (map[key]) return map[key];
  return key
    .replace(/_pct$/, ' %')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v, key) {
  if (v == null) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  if (key.endsWith('_pct') || key.endsWith('_rate') || key === 'engagement') {
    return `${n.toFixed(1)}%`;
  }
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(2);
}

function polarityColor(percentile, metricKey) {
  if (percentile == null) return 'bg-gray-100 text-gray-700 border-gray-200';
  const flipped = NEGATIVE_METRICS.has(metricKey);
  const effective = flipped ? 100 - percentile : percentile;
  if (effective >= 60) return 'bg-green-50 text-green-700 border-green-200';
  if (effective >= 30) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

function PercentileBar({ row }) {
  const { p25, p50, p75, tenant_value } = row;
  const min = Math.min(Number(p25 ?? 0), Number(tenant_value ?? 0));
  const max = Math.max(Number(p75 ?? 100), Number(tenant_value ?? 0));
  const range = max - min || 1;
  const pct = (v) => Math.max(0, Math.min(100, ((Number(v) - min) / range) * 100));
  const tenantPct = pct(tenant_value);

  return (
    <div className="relative h-8 w-full">
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-gray-100" />
      <div
        className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-blue-200 rounded-full"
        style={{ left: `${pct(p25)}%`, width: `${pct(p75) - pct(p25)}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-blue-600"
        style={{ left: `${pct(p50)}%` }}
        title={`Median (p50): ${formatValue(p50, row.metric_key)}`}
      />
      {tenant_value != null && (
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gray-900 border-2 border-white shadow"
          style={{ left: `calc(${tenantPct}% - 6px)` }}
          title={`You: ${formatValue(tenant_value, row.metric_key)}`}
        />
      )}
      <div className="absolute -top-0.5 left-0 text-[10px] text-gray-400">p25 {formatValue(p25, row.metric_key)}</div>
      <div className="absolute -top-0.5 right-0 text-[10px] text-gray-400">p75 {formatValue(p75, row.metric_key)}</div>
    </div>
  );
}

function SettingsCard({ settings, onSave, saving }) {
  const [optedIn, setOptedIn] = useState(false);
  const [industry, setIndustry] = useState('');
  const [sizeBand, setSizeBand] = useState('');

  useEffect(() => {
    if (settings) {
      setOptedIn(!!settings.opted_in);
      setIndustry(settings.industry || '');
      setSizeBand(settings.company_size_band || '');
    }
  }, [settings]);

  const dirty =
    settings &&
    (optedIn !== !!settings.opted_in ||
      industry !== (settings.industry || '') ||
      sizeBand !== (settings.company_size_band || ''));

  return (
    <div className="card p-5 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="section-title">Benchmarking settings</h2>
          <p className="text-sm text-gray-500 mt-1">
            Opt in to contribute anonymised metrics and see how you stack up against peers.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={optedIn}
            onChange={(e) => setOptedIn(e.target.checked)}
          />
          <div className="relative w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-blue-600 transition-colors">
            <div
              className={clsx(
                'absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform',
                optedIn && 'translate-x-5',
              )}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">{optedIn ? 'Opted in' : 'Opted out'}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Industry</label>
          <input
            className="input"
            placeholder="e.g. SaaS, Fintech, Healthcare"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            disabled={!optedIn}
          />
        </div>
        <div>
          <label className="label">Company size</label>
          <select
            className="input"
            value={sizeBand}
            onChange={(e) => setSizeBand(e.target.value)}
            disabled={!optedIn}
          >
            <option value="">Select a size band…</option>
            {SIZE_BANDS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={() => onSave({ opted_in: optedIn, industry: industry || null, company_size_band: sizeBand || null })}
          disabled={!dirty || saving}
          className="btn-primary disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}

export default function Benchmarks() {
  const qc = useQueryClient();
  const [segment, setSegment] = useState('peer');

  const { data: settings, isLoading: loadingSettings } = useQuery({
    queryKey: ['benchmarks', 'settings'],
    queryFn:  () => benchmarkApi.settings().then((r) => r.data),
  });

  const { data: comparison, isLoading: loadingComparison, isFetching: fetchingComparison } = useQuery({
    queryKey: ['benchmarks', 'comparison', segment],
    queryFn:  () => benchmarkApi.comparison(segment).then((r) => r.data),
    enabled:  !!settings,
  });

  const saveSettings = useMutation({
    mutationFn: benchmarkApi.updateSettings,
    onSuccess:  () => {
      toast.success('Settings saved');
      qc.invalidateQueries({ queryKey: ['benchmarks'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to save settings'),
  });

  const refresh = useMutation({
    mutationFn: benchmarkApi.refresh,
    onSuccess:  () => {
      toast.success('Benchmark refresh queued');
      qc.invalidateQueries({ queryKey: ['benchmarks', 'comparison'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to queue refresh'),
  });

  if (loadingSettings) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  const rows = comparison?.rows || [];
  const hasMessage = comparison?.message && rows.length === 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Industry Benchmarks"
        subtitle="Anonymised peer comparisons across core performance, retention, and calibration metrics."
        actions={
          <button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="btn-secondary"
          >
            <ArrowPathIcon className={clsx('w-4 h-4', refresh.isPending && 'animate-spin')} />
            {refresh.isPending ? 'Queuing…' : 'Refresh benchmarks'}
          </button>
        }
      />

      <SettingsCard
        settings={settings}
        onSave={(d) => saveSettings.mutate(d)}
        saving={saveSettings.isPending}
      />

      {!settings?.opted_in && (
        <div className="card p-5 flex items-start gap-3 bg-blue-50 border-blue-200">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Benchmarks are off</p>
            <p className="text-sm text-blue-800 mt-0.5">
              Opt in above to start seeing peer comparisons. Your raw data is never shared — only
              aggregated, anonymised metrics.
            </p>
          </div>
        </div>
      )}

      {settings?.opted_in && (
        <>
          {/* Segment selector */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {SEGMENTS.map((s) => {
              const active = segment === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSegment(s.key)}
                  className={clsx(
                    'card p-4 text-left transition-all border',
                    active
                      ? 'border-blue-500 ring-2 ring-blue-100'
                      : 'border-transparent hover:border-gray-200',
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className={clsx('w-4 h-4', active ? 'text-blue-600' : 'text-gray-400')} />
                    <span className={clsx('text-sm font-semibold', active ? 'text-blue-700' : 'text-gray-700')}>
                      {s.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{s.desc}</p>
                </button>
              );
            })}
          </div>

          {comparison && (
            <div className="card p-4 mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <CheckCircleIcon className="w-4 h-4 text-green-500" />
                Period: <strong className="text-gray-900">{comparison.period || '—'}</strong>
              </span>
              <span>Industry: <strong className="text-gray-900">{comparison.industry || 'Any'}</strong></span>
              <span>Size: <strong className="text-gray-900">{comparison.company_size || 'Any'}</strong></span>
              {rows[0]?.sample_size != null && (
                <span>Sample: <strong className="text-gray-900">{rows[0].sample_size}</strong> tenants</span>
              )}
            </div>
          )}

          {loadingComparison || fetchingComparison ? (
            <div className="flex justify-center p-20"><Spinner size="lg" /></div>
          ) : hasMessage ? (
            <div className="card p-5 flex items-start gap-3 bg-amber-50 border-amber-200">
              <InformationCircleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">No benchmark data yet</p>
                <p className="text-sm text-amber-800 mt-0.5">{comparison.message}</p>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No comparison available"
              description="We don’t have enough data for this segment yet. Try a broader segment or queue a refresh."
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">Metric</th>
                    <th className="th">Your value</th>
                    <th className="th">Percentile</th>
                    <th className="th w-80">Distribution (p25 · p50 · p75)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {rows.map((row) => {
                    const pct = row.percentile;
                    return (
                      <tr key={row.metric_key} className="tr-hover">
                        <td className="td">
                          <div className="font-medium text-gray-900">{humaniseMetric(row.metric_key)}</div>
                          <div className="text-xs text-gray-500 font-mono">{row.metric_key}</div>
                        </td>
                        <td className="td">
                          <span className={clsx(
                            'inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold border',
                            polarityColor(pct, row.metric_key),
                          )}>
                            {formatValue(row.tenant_value, row.metric_key)}
                          </span>
                        </td>
                        <td className="td">
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold text-gray-900">
                              {pct != null ? pct : '—'}
                            </span>
                            {pct != null && <span className="text-xs text-gray-500">pctl</span>}
                          </div>
                        </td>
                        <td className="td">
                          <PercentileBar row={row} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
